import { HttpClient } from '../api/HttpClient';
import type {
  Language,
  LanguagesQueryParams,
  LanguagesData,
  CreateLanguageRequest,
  UpdateLanguageRequest,
} from '../../types/api/language';

/**
 * Language Service
 * 
 * Handles all language-related API operations for admin functionality
 * 
 * Features:
 * - Language listing with pagination and filtering
 * - Language CRUD operations
 * - Search functionality
 */
export class LanguageService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get languages list with pagination and filters
   */
  async getLanguages(params: LanguagesQueryParams = {}): Promise<LanguagesData> {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    
    // Add filter parameters
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }

    const url = `/admin/languages?${queryParams.toString()}`;
    const response = await this.httpClient.get<LanguagesData>(url);
    
    // Check if API request was successful
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch languages');
    }
    
    return response.data;
  }

  /**
   * Get language by ID
   */
  async getLanguage(languageId: string): Promise<Language> {
    const response = await this.httpClient.get<Language>(`/admin/languages/${languageId}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch language');
    }
    
    return response.data;
  }

  /**
   * Create a new language with optional icon file upload
   */
  async createLanguage(payload: { data: CreateLanguageRequest; iconFile?: File | Blob | null }): Promise<Language> {
    const formData = this.buildLanguageFormData(payload.data, payload.iconFile);
    const response = await this.httpClient.post<Language>(`/admin/languages`, formData);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create language');
    }
    return response.data;
  }

  /**
   * Update language information with optional icon file upload
   */
  async updateLanguage(
    languageId: string,
    payload: { data: UpdateLanguageRequest; iconFile?: File | Blob | null }
  ): Promise<Language> {
    const formData = this.buildLanguageFormData(payload.data, payload.iconFile);
    const response = await this.httpClient.put<Language>(`/admin/languages/${languageId}`, formData);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update language');
    }
    return response.data;
  }

  /**
   * Delete a language
   */
  async deleteLanguage(languageId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      `/admin/languages/${languageId}`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete language');
    }
  }

  /**
   * Build FormData for language create/update with icon file
   */
  private buildLanguageFormData(
    data: CreateLanguageRequest | UpdateLanguageRequest,
    iconFile?: File | Blob | null
  ): FormData {
    const formData = new FormData();
    const dataBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', dataBlob, 'data.json');

    if (iconFile) {
      const fileName =
        iconFile instanceof File && iconFile.name
          ? iconFile.name
          : (iconFile as any)?.name || 'icon';
      formData.append('icon', iconFile, fileName);
    }
    return formData;
  }
}

